import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Vote, AlertCircle, CheckCircle2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import SongCard from '@/components/SongCard';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';
import type { Submission } from '@/lib/mock-data';
import { usePlayback } from '@/lib/playback';
import { extractYouTubeId, getYouTubeThumbnail } from '@/lib/utils';
import * as api from '@/lib/api';

const VoteRound = () => {
  const { leagueId, roundId } = useParams<{ leagueId: string; roundId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state, getSubmissionsByRound, getVotesByUserAndRound, hasSubmittedVotes, createVote, updateLeague, updateRound, addSubmissionsToState } = useStore();
  const { currentUser } = state;
  const playback = usePlayback();
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Find the league and round
  const league = useMemo(() => {
    return state.leagues.find(l => l.id === leagueId);
  }, [state.leagues, leagueId]);
  
  const round = useMemo(() => {
    return state.rounds.find(r => r.id === roundId);
  }, [state.rounds, roundId]);

  // Fetch league and round from Supabase if not in local state
  // Redirect to dashboard if not found
  useEffect(() => {
    if (!leagueId || !roundId) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };

    if (!isSupabaseConfigured()) return;

    // If we have both league and round, we're good
    if (league && round) return;

    setIsLoadingData(true);
    
    const fetchData = async () => {
      try {
        // Fetch league if missing
        if (!league && leagueId) {
          const fetchedLeague = await api.leagueAPI.getLeague(leagueId);
          if (!fetchedLeague) {
            console.log('[VoteRound] League not found, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
          updateLeague(fetchedLeague.id, fetchedLeague);
        }

        // Fetch round if missing
        if (!round && roundId) {
          const fetchedRound = await api.roundAPI.getRound(roundId);
          if (!fetchedRound) {
            console.log('[VoteRound] Round not found, redirecting to dashboard');
            navigate('/dashboard', { replace: true });
            return;
          }
          updateRound(fetchedRound.id, fetchedRound);
        }

        // Fetch submissions for this round
        const submissions = await api.submissionAPI.getSubmissionsByRound(roundId);
        if (submissions.length > 0) {
          addSubmissionsToState(submissions);
        }
      } catch (error) {
        console.error('Failed to fetch league/round/submissions:', error);
        // On error, redirect to dashboard
        navigate('/dashboard', { replace: true });
      } finally {
        setIsLoadingData(false);
      }
    };

    // Fetch immediately if data is missing
    if (!league || !round) {
      fetchData();
    } else {
      setIsLoadingData(false);
    }
  }, [leagueId, roundId, league, round, updateLeague, updateRound, addSubmissionsToState, navigate]);

  // Get submissions for this round
  const submissions = useMemo(() => {
    if (!roundId) return [];
    return getSubmissionsByRound(roundId);
  }, [roundId, getSubmissionsByRound]);
  
  // Check if user has already submitted votes (locked in)
  const hasSubmitted = useMemo(() => {
    if (!roundId || !currentUser?.id) return false;
    return hasSubmittedVotes(currentUser.id, roundId);
  }, [roundId, currentUser?.id, hasSubmittedVotes]);
  
  // Get existing votes (only drafts - is_submitted = false)
  const existingVotes = useMemo(() => {
    if (!roundId || !currentUser?.id) return [];
    const allVotes = getVotesByUserAndRound(currentUser.id, roundId);
    // Only load draft votes (is_submitted = false) for editing
    return allVotes.filter(vote => vote.is_submitted !== true);
  }, [roundId, currentUser?.id, getVotesByUserAndRound]);
  
  // Initialize user votes from existing draft votes only
  const [userVotes, setUserVotes] = useState<Record<string, number>>({});
  
  useEffect(() => {
    if (existingVotes.length > 0) {
      const votesMap: Record<string, number> = {};
      existingVotes.forEach(vote => {
        votesMap[vote.submission_id] = vote.points;
      });
      setUserVotes(votesMap);
    }
  }, [existingVotes]);

  // Redirect to results if user has already submitted votes
  useEffect(() => {
    if (hasSubmitted && roundId && leagueId) {
      navigate(`/leagues/${leagueId}/rounds/${roundId}/results`, { replace: true });
    }
  }, [hasSubmitted, roundId, leagueId, navigate]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter out user's own submission
  const voteableSubmissions = useMemo(() => {
    if (!currentUser?.id) {
      return submissions;
    }
    return submissions.filter(s => s.user_id !== currentUser.id);
  }, [submissions, currentUser?.id]);

  const playlistTracks = useMemo(() => {
    const tracks = submissions
      .map((submission) => {
        const youtubeId =
          submission.youtube_video_id ?? extractYouTubeId(submission.video_url ?? '');
        console.log('[VoteRound] Processing submission for playlist', {
          submissionId: submission.id,
          songTitle: submission.song_title,
          youtube_video_id: submission.youtube_video_id,
          video_url: submission.video_url,
          extractedYoutubeId: youtubeId,
        });
        if (!youtubeId) {
          return null;
        }
        return {
          submissionId: submission.id,
          roundId: submission.round_id,
          leagueId: league?.id,
          title: submission.song_title,
          artist: submission.artist_name,
          youtubeId,
          submitter: submission.username || 'Unknown DJ',
          comment: submission.submitter_comment,
          thumbnail: submission.video_thumbnail || getYouTubeThumbnail(youtubeId, 'mqdefault'),
        };
      })
      .filter((track): track is NonNullable<typeof track> => Boolean(track));
    console.log('[VoteRound] Playlist tracks', { totalSubmissions: submissions.length, playableTracks: tracks.length });
    return tracks;
  }, [league?.id, submissions]);

  // Show loading state while fetching data (after all hooks are called)
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading round data...</p>
        </div>
      </div>
    );
  }

  // Don't show "not found" immediately - let the redirect logic handle it
  // This prevents flashing the error message before redirect
  if (!league || !round) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const deadline = new Date(round.voting_deadline);
  const now = new Date();
  const isDeadlinePassed = now > deadline;
  const isNotVotingStatus = round.status !== 'voting';

  const totalPointsAllocated = Object.values(userVotes).reduce((sum, points) => sum + points, 0);
  const pointsRemaining = league.points_per_round - totalPointsAllocated;
  const hasAllocatedAllPoints = totalPointsAllocated === league.points_per_round;
  const getTimeRemaining = () => {
    if (isDeadlinePassed) return 'Deadline has passed';

    const diff = deadline.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
    return `${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
  };

  const handleVote = (submissionId: string, points: number) => {
    if (hasSubmitted || isDeadlinePassed || isNotVotingStatus) return;

    const currentPoints = userVotes[submissionId] || 0;
    const newTotal = totalPointsAllocated - currentPoints + points;

    if (newTotal > league.points_per_round) {
      toast({
        title: "Not enough points",
        description: `You only have ${pointsRemaining + currentPoints} points remaining to allocate.`,
        variant: "destructive",
      });
      return;
    }

    setUserVotes((prev) => ({
      ...prev,
      [submissionId]: points,
    }));

    // Auto-save draft vote (is_submitted = false)
    if (currentUser && roundId) {
      createVote({
        round_id: roundId,
        voter_id: currentUser.id,
        submission_id: submissionId,
        points: points,
        is_submitted: false, // Draft vote - can be edited
      });
      // Note: Auto-save happens silently. User can see their progress in the UI.
    }
  };

  const handleSubmitVotes = async () => {
    if (!hasAllocatedAllPoints) {
      toast({
        title: "Allocate all points",
        description: `Please allocate all ${league.points_per_round} points before submitting your votes.`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Update all draft votes to submitted (is_submitted = true)
      Object.entries(userVotes).forEach(([submissionId, points]) => {
        if (points > 0 && currentUser) {
          // Create/update vote with is_submitted = true
          createVote({
            round_id: round.id,
            voter_id: currentUser.id,
            submission_id: submissionId,
            points: points,
            is_submitted: true, // Lock in the vote
          });
        }
      });

      toast({
        title: "Votes submitted! 🗳️",
        description: "Your votes have been locked in and cannot be changed.",
      });

      // Navigate to in-progress results after a short delay
      setTimeout(() => {
        navigate(`/leagues/${leagueId}/rounds/${roundId}/results`);
      }, 1500);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit votes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canVote = !hasSubmitted && !isDeadlinePassed && !isNotVotingStatus && voteableSubmissions.length > 0;

  if (!currentUser) {
    return null;
  }

  // If user has already submitted, show a message and redirect (this should be caught by useEffect above, but just in case)
  if (hasSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-900/20">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Your votes have been submitted. Redirecting to results...
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate(`/leagues/${leagueId}/rounds/${roundId}/results`)}>
            View Results
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
      <Navigation />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(`/leagues/${leagueId}`)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to League
          </Button>
          
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Vote on Submissions</h1>
              <p className="text-muted-foreground">
                {round.theme}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[VoteRound] Play all clicked', { trackCount: playlistTracks.length });
                  try {
                    playback.setPlaylist(playlistTracks, {
                      id: `round_${round.id}_playlist`,
                      label: `${league?.name || 'Round'} • ${round.theme}`,
                      autoplay: false,
                      shuffle: false,
                    });
                    console.log('[VoteRound] Playlist set successfully, navigating to player');
                    // Navigate to playlist player page
                    navigate('/playlists/player');
                  } catch (error) {
                    console.error('[VoteRound] Error setting playlist:', error);
                  }
                }}
                disabled={playlistTracks.length === 0}
                title={playlistTracks.length === 0 ? `No playable tracks (${submissions.length} submissions, ${submissions.filter(s => !s.youtube_video_id && !s.video_url).length} without YouTube links)` : `Play all ${playlistTracks.length} tracks`}
              >
                <Play className="mr-2 h-4 w-4" />
                Play all {playlistTracks.length > 0 && `(${playlistTracks.length})`}
              </Button>
              {playlistTracks.length === 0 && submissions.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {submissions.length} submission{submissions.length !== 1 ? 's' : ''} • No YouTube links
                </Badge>
              )}
              {hasSubmitted && (
                <Badge variant="default" className="bg-green-500">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Votes Submitted
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Round Info Card */}
        <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-br from-purple-50/30 to-blue-50/30 dark:from-purple-950/15 dark:to-blue-950/15 shadow-md">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Theme</p>
                <p className="text-lg font-semibold text-foreground">{round.theme}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Voting Deadline</p>
                <p className={`text-sm font-medium ${
                  isDeadlinePassed ? 'text-destructive' : 'text-foreground'
                }`}>
                  {deadline.toLocaleString()} ({getTimeRemaining()})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Voting Instructions & Status */}
        <Card className={`mb-6 border-2 ${hasAllocatedAllPoints && canVote ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10' : 'border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20'} shadow-md`}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Points Allocation</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You have <strong className="text-foreground">{league.points_per_round} points</strong> to distribute among the submissions. 
                  Allocate points based on how well each song fits the theme.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Points allocated</span>
                    <span className={`font-semibold ${
                      hasAllocatedAllPoints ? 'text-green-600 dark:text-green-400' : 'text-foreground'
                    }`}>
                      {totalPointsAllocated} / {league.points_per_round}
                    </span>
                  </div>
                  <Progress 
                    value={(totalPointsAllocated / league.points_per_round) * 100} 
                    className="h-2"
                  />
                  {pointsRemaining > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {pointsRemaining} point{pointsRemaining !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                  {hasAllocatedAllPoints && canVote && (
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                      ✓ All points allocated! Ready to submit.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {canVote && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground text-center">
                  Your progress is automatically saved as you vote. Click "Submit Votes" to lock in your choices.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      // Manually save all current votes as drafts
                      if (currentUser && roundId) {
                        let savedCount = 0;
                        Object.entries(userVotes).forEach(([submissionId, points]) => {
                          if (points > 0) {
                            createVote({
                              round_id: roundId,
                              voter_id: currentUser.id,
                              submission_id: submissionId,
                              points: points,
                              is_submitted: false, // Draft vote
                            });
                            savedCount++;
                          }
                        });
                        toast({
                          title: "Progress saved",
                          description: savedCount > 0 
                            ? `Saved ${savedCount} vote${savedCount !== 1 ? 's' : ''} as draft.`
                            : "No votes to save yet.",
                        });
                      }
                    }}
                    className="flex-1"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save Progress
                  </Button>
                  <Button
                    onClick={handleSubmitVotes}
                    disabled={!hasAllocatedAllPoints || isSubmitting}
                    className={`flex-1 btn-music ${hasAllocatedAllPoints ? 'animate-pulse' : ''}`}
                  >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Submitting votes...
                    </>
                  ) : (
                    <>
                      <Vote className="h-4 w-4 mr-2" />
                      Submit Votes (Lock In)
                    </>
                  )}
                </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts */}
        {isDeadlinePassed && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The voting deadline has passed. You can no longer vote for this round.
            </AlertDescription>
          </Alert>
        )}

        {isNotVotingStatus && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This round is no longer accepting votes. The current status is: {round.status}.
            </AlertDescription>
          </Alert>
        )}

        {voteableSubmissions.length === 0 && submissions.length > 0 && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              There are no other submissions to vote on yet, or you are the only one who has submitted.
            </AlertDescription>
          </Alert>
        )}

        {submissions.length === 0 && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No submissions yet. Check back later when members have submitted their songs.
            </AlertDescription>
          </Alert>
        )}

        {hasSubmitted && (
          <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-900/20">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Your votes have been submitted and locked in! You can view the in-progress results, but you cannot change your votes.
            </AlertDescription>
          </Alert>
        )}

        {/* Submissions */}
        {submissions.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Submissions ({voteableSubmissions.length})
            </h2>
            {hasSubmitted ? (
              // Show in-progress results when user has submitted
              <div className="grid gap-6">
                {voteableSubmissions.sort((a, b) => b.total_points - a.total_points).map((submission) => (
                  <SongCard
                    key={submission.id}
                    submission={submission}
                    showSubmitter={true}
                    showPoints={true}
                    isVoting={false}
                    userPoints={userVotes[submission.id] || 0}
                    leagueId={leagueId}
                  />
                ))}
              </div>
            ) : (
              // Show voting interface when user hasn't submitted
              <div className="space-y-4">
                {submissions.length > 0 && playlistTracks.length === 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>No playable tracks:</strong> None of the {submissions.length} submission{submissions.length !== 1 ? 's' : ''} have YouTube video links. 
                      Submissions need either a <code>youtube_video_id</code> or <code>video_url</code> to be playable.
                      <div className="mt-2 text-xs">
                        Submissions with YouTube links: {submissions.filter(s => s.youtube_video_id || s.video_url).length} / {submissions.length}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid gap-6">
                  {voteableSubmissions.sort((a, b) => b.total_points - a.total_points).map((submission) => (
                    <SongCard
                      key={submission.id}
                      submission={submission}
                      showSubmitter={true}
                      showPoints={true}
                      isVoting={canVote}
                      onVote={handleVote}
                      userPoints={userVotes[submission.id] || 0}
                      leagueId={leagueId}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Voting Guidelines */}
        <Card className="mt-6 border-2 border-secondary/30 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/15 dark:to-indigo-950/15 shadow-md">
          <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-secondary/30">
            <CardTitle className="text-blue-600 dark:text-blue-400">Voting Guidelines</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>You must allocate all <strong className="text-foreground">{league.points_per_round} points</strong> before submitting</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Consider how well each song fits the theme and its overall quality</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>You can give multiple points to a single submission, or spread them across multiple songs</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Once you submit your votes, you cannot change them</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>You cannot vote for your own submission</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Music Platform Links */}
        {playlistTracks.length > 0 && (
          <Card className="mt-6 border-2 border-primary/20 bg-gradient-to-br from-purple-50/30 to-blue-50/30 dark:from-purple-950/15 dark:to-blue-950/15 shadow-md">
            <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/20">
              <CardTitle className="text-primary">Listen on Music Platforms</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-3">
                {(() => {
                  const spotifyUrl = api.generateSpotifyPlaylistUrl(submissions);
                  const youtubeMusicUrl = playlistTracks.length > 0 
                    ? `https://music.youtube.com/playlist?list=${round.id}` 
                    : null;
                  
                  // Generate Apple Music search URL (requires manual search)
                  const appleMusicSearchQuery = submissions
                    .map(s => `${s.song_title} ${s.artist_name}`)
                    .join(', ');
                  const appleMusicUrl = appleMusicSearchQuery 
                    ? `https://music.apple.com/search?term=${encodeURIComponent(appleMusicSearchQuery)}`
                    : null;
                  
                  return (
                    <>
                      {spotifyUrl && (
                        <Button variant="outline" asChild className="flex items-center gap-2">
                          <a href={spotifyUrl} target="_blank" rel="noopener noreferrer">
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                            </svg>
                            Open in Spotify
                          </a>
                        </Button>
                      )}
                      {youtubeMusicUrl && (
                        <Button variant="outline" asChild className="flex items-center gap-2">
                          <a href={youtubeMusicUrl} target="_blank" rel="noopener noreferrer">
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            YouTube Music
                          </a>
                        </Button>
                      )}
                      {appleMusicUrl && (
                        <Button variant="outline" asChild className="flex items-center gap-2">
                          <a href={appleMusicUrl} target="_blank" rel="noopener noreferrer">
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                            Apple Music
                          </a>
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                {playlistTracks.length} track{playlistTracks.length !== 1 ? 's' : ''} available
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default VoteRound;


